/**
 * Image/video preview dialog for Storage on the shared Modal: Escape closes, focus moves in on open and back to
 * the trigger on close, labelled close button. Left/right arrows (and buttons) step through previewable files.
 */

import { useEffect, useMemo } from 'react'
import { Download, ChevronLeft, ChevronRight } from 'lucide-react'
import { API_BASE } from '../../lib/api-client'
import { useT } from '../../i18n'
import { Modal } from '../ui/modal'
import { buttonSecondaryClass, iconButtonClass } from '../ui/styles'
import type { StorageFile } from '../../hooks/use-storage'

interface StoragePreviewModalProps {
  file: StorageFile | null
  files: StorageFile[]
  onClose: () => void
  onNavigate: (file: StorageFile) => void
}

/** Check if a content type is previewable */
export function isPreviewable(contentType: string): boolean {
  return contentType.startsWith('image/') || contentType.startsWith('video/')
}

export function StoragePreviewModal({ file, ...rest }: StoragePreviewModalProps) {
  // Mount the dialog only while a file is open so Modal's focus-in / focus-return runs per opening
  if (!file) return null
  return <PreviewDialog file={file} {...rest} />
}

function PreviewDialog({ file, files, onClose, onNavigate }: StoragePreviewModalProps & { file: StorageFile }) {
  const t = useT()
  const previewable = useMemo(() => files.filter((f) => isPreviewable(f.contentType)), [files])
  const index = previewable.findIndex((f) => f.id === file.id)
  const prev = index > 0 ? previewable[index - 1] : null
  const next = index !== -1 && index < previewable.length - 1 ? previewable[index + 1] : null

  // Arrow keys step through files (Modal already handles Escape)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      // Leave arrows to the video player controls when they have focus
      if (e.target instanceof HTMLVideoElement) return
      if (e.key === 'ArrowLeft' && prev) onNavigate(prev)
      if (e.key === 'ArrowRight' && next) onNavigate(next)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [prev, next, onNavigate])

  const fileUrl = `${API_BASE}/api/files/${file.fileKey}`
  const isImage = file.contentType.startsWith('image/')
  const isVideo = file.contentType.startsWith('video/')

  return (
    <Modal title={file.filename} onClose={onClose} widthClass="max-w-5xl">
      <div className="flex flex-col items-center gap-3">
        <div className="flex max-h-[65vh] w-full items-center justify-center overflow-hidden rounded-lg bg-surface-muted">
          {isImage && <img key={file.id} src={fileUrl} alt={file.description ?? file.filename} className="max-h-[65vh] max-w-full object-contain" />}
          {isVideo && (
            <video key={file.id} src={fileUrl} controls autoPlay muted className="max-h-[65vh] max-w-full">
              <track kind="captions" />
            </video>
          )}
        </div>
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => prev && onNavigate(prev)}
              disabled={!prev}
              aria-label={t('storage.preview.previous')}
              title={t('storage.preview.previous')}
              className={iconButtonClass}
            >
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            {previewable.length > 1 && index !== -1 && (
              <span className="min-w-[4rem] text-center text-sm text-fg-muted" aria-live="polite">
                {t('storage.preview.position', { current: index + 1, total: previewable.length })}
              </span>
            )}
            <button
              type="button"
              onClick={() => next && onNavigate(next)}
              disabled={!next}
              aria-label={t('storage.preview.next')}
              title={t('storage.preview.next')}
              className={iconButtonClass}
            >
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
          <a href={fileUrl} download={file.filename} className={buttonSecondaryClass}>
            <Download className="h-4 w-4" aria-hidden="true" />
            {t('storage.file.download')}
          </a>
        </div>
      </div>
    </Modal>
  )
}
